import gsap from 'gsap';
import { Draggable } from 'gsap/Draggable';
import InertiaPlugin from 'gsap/InertiaPlugin';
import Viewport from '../core/Viewport';

gsap.registerPlugin(Draggable);
gsap.registerPlugin(InertiaPlugin);

export default (el, opts = {}) => {

    const { afterFocusScroll = null, preventFocusOnPressed = true } = opts || {};

    let draggable;
    let observer;

    const onMove = () => {
        el.dispatchEvent(new CustomEvent('move', {
            detail: {
                x: draggable.x
            },
            bubbles: true
        }));
    };

    const onMoveEnd = () => {
        el.dispatchEvent(new CustomEvent('moveend', {
            detail: {
                x: draggable.x
            },
            bubbles: true
        }));
    };

    const scrollToX = (x, vars = {}) => {
        if (!draggable.enabled()) {
            return;
        }
        const { x: currentX, minX } = draggable;
        let { duration = null } = vars;
        let toX = x;
        if (toX < 0) {
            toX = 0;
        }
        toX *= -1;
        if (toX > 0) {
            toX = 0;
        } else if (toX < minX) {
            toX = minX;
        }
        if (Math.abs(toX - currentX) === 0) {
            duration = 0;
        } else if (duration === null) {
            duration = 0.3;
        }
        const { onUpdate = null, onComplete = null } = vars;
        delete vars.onUpdate;
        delete vars.onComplete;
        gsap.to(el, {
            ease: 'Power2.easeInOut',
            ...vars,
            x: toX,
            duration,
            onUpdate() {
                draggable.update();
                if (onUpdate) {
                    onUpdate(this);
                }
                onMove();
            },
            onComplete() {
                draggable.update();
                if (onComplete) {
                    onComplete(this);
                }
                onMoveEnd();
            }
        });
    };

    const scrollToNode = (node, vars = {}, center = false) => {
        if (!draggable.enabled()) {
            return;
        }
        const { left: nodeLeft, width: nodeWidth } = node.getBoundingClientRect();
        const { left: draggableLeft } = el.getBoundingClientRect();
        const { width: parentWidth } = el.parentNode.getBoundingClientRect();
        let x = (nodeLeft - draggableLeft);
        if (center) {
            x -= ((parentWidth - nodeWidth) / 2);
        }
        scrollToX(x, vars);
    };

    const scrollTo = (thing, vars) => {
        if (!draggable.enabled()) {
            return;
        }
        if (thing instanceof Element) {
            scrollToNode(thing, vars);
        } else {
            scrollToX(thing, vars);
        }
    };

    const resetWidth = () => {
        gsap.set([el, ...el.children], { clearProps: 'width' });
    };

    const setWidth = () => {
        resetWidth();

        [...el.children].forEach(node => {
            const { width: nodeWidth } = node.getBoundingClientRect();
            gsap.set(node, { width: Math.ceil(nodeWidth) });
        });
        gsap.set(el, { width: Math.ceil(el.scrollWidth) });
    };

    const onResize = () => {
        if (el.offsetParent === null) {
            resetWidth();
            return;
        }

        setWidth();
        draggable.applyBounds(el.parentNode);

        if (Math.abs(draggable.minX) <= 0) {
            draggable.disable();
        } else {
            draggable.enable();
        }
    };

    const onFocusIn = e => {
        if (!draggable.enabled()) {
            return;
        }
        if (preventFocusOnPressed && draggable.isPressed) {
            return;
        }
        const overflowingParent = el.closest('.overflow-hidden') || el.parentNode;
        overflowingParent.scrollLeft = 0;
        // Is the element fully visible in the container?
        const parent = el.parentNode;
        const parentRect = parent.getBoundingClientRect();
        const targetRect = e.target.getBoundingClientRect();
        const isFullyVisible = targetRect.left >= parentRect.left && targetRect.right <= parentRect.right;
        if (isFullyVisible) {
            if (afterFocusScroll) {
                afterFocusScroll(e);
            }
            return;
        }
        const vars = {};
        if (afterFocusScroll) {
            vars.onComplete = () => {
                afterFocusScroll(e);
            };
        }
        scrollToNode(e.target, vars, true);
    };

    const destroy = () => {
        el.removeEventListener('focusin', onFocusIn);
        if (draggable) {
            draggable.removeEventListener('drag', onMove);
            draggable.removeEventListener('throwupdate', onMove);
            draggable.removeEventListener('throwend', onMoveEnd);
            draggable.kill();
            draggable = null;
        }
        Viewport.off('resize', onResize);
    };

    const init = () => {
        [draggable] = Draggable.create(el, {
            bounds: el.parentNode,
            type: 'x',
            inertia: true,
            allowNativeTouchScrolling: true,
            allowContextMenu: false,
            dragClickables: true,
            activeCursor: 'grabbing',
            force3D: true,
            zIndexBoost: false,
            edgeResistance: 0.95
        });

        draggable.addEventListener('drag', onMove);
        draggable.addEventListener('throwupdate', onMove);
        draggable.addEventListener('throwcomplete', onMoveEnd);

        observer = new IntersectionObserver(([{ isIntersecting }]) => {
            if (!isIntersecting) {
                return;
            }
            onResize();
        });
        observer.observe(el);

        onResize();

        el.addEventListener('focusin', onFocusIn);
        Viewport.on('resize', onResize);
    };

    init();

    return {
        draggable,
        scrollTo,
        scrollToNode,
        destroy
    };

};
